import { useEffect, useMemo, useRef, useState, type FormEvent } from 'react';
import { Link, useNavigate, useParams } from 'react-router';
import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { CAMPAIGN_PLACEHOLDERS, RECIPIENT_STATUSES, RECIPIENT_STATUS_LABELS, type AudiencePreview, type Campaign, type CampaignAudience, type CampaignRecipient, type Paginated } from '@kontor/shared';
import { api, queryString } from '../../api';
import { useSession } from '../../session';
import { RelationPicker } from '../contacts/relationTypes';
import { CampaignBadge, RecipientBadge, day, errorText, when } from './shared';
import './marketing.css';

const EMPTY_AUDIENCE: CampaignAudience = { relationTypeIds: [], kinds: [], postalCodePrefix: undefined };
const percent = (part: number, whole: number) => (whole > 0 ? `${((part / whole) * 100).toLocaleString('de-DE', { maximumFractionDigits: 1 })} %` : '–');

/** Vorschau: so liest sich der Text mit eingesetzten Platzhaltern */
function preview(text: string, firma: string): string {
  return text.replace(/\{\{\s*name\s*\}\}/gi, 'Max Mustermann').replace(/\{\{\s*firma\s*\}\}/gi, firma);
}

/**
 * Eine Kampagne (KON-170): Als Entwurf ein Formular mit Text, Verteiler, Vorschau und Testversand. Nach dem Versand eine Bilanz mit der Liste der Empfänger. Gesendete
 * Kampagnen sind gesperrt, wer sie wiederverwenden möchte, dupliziert sie.
 */
export function CampaignPage() {
  const { id } = useParams();
  const isNew = !id;
  const navigate = useNavigate();
  const client = useQueryClient();
  const { can, session } = useSession();
  const [name, setName] = useState('');
  const [subject, setSubject] = useState('');
  const [body, setBody] = useState('');
  const [audience, setAudience] = useState<CampaignAudience>(EMPTY_AUDIENCE);
  const [testTo, setTestTo] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [recipientStatus, setRecipientStatus] = useState('');
  const [recipientPage, setRecipientPage] = useState(1);
  const bodyField = useRef<HTMLTextAreaElement>(null);
  const loaded = useRef<string | null>(null);

  const campaign = useQuery({
    queryKey: ['marketing-campaign', id],
    queryFn: async () => (await api<{ data: Campaign }>('GET', `/marketing/campaigns/${id}`)).data,
    enabled: !isNew,
    refetchInterval: (q) => (q.state.data?.status === 'sending' ? 2000 : false),
  });
  const c = campaign.data;
  const editable = isNew || c?.status === 'draft';

  // Formular einmal aus dem gespeicherten Stand füllen
  useEffect(() => {
    if (!c || loaded.current === `${c.id}:${c.updatedAt}`) return;
    loaded.current = `${c.id}:${c.updatedAt}`;
    setName(c.name);
    setSubject(c.subject);
    setBody(c.body);
    setAudience(c.audience);
  }, [c]);

  const audienceKey = JSON.stringify(audience);
  const reach = useQuery({
    queryKey: ['marketing-audience', audienceKey],
    queryFn: async () => (await api<{ data: AudiencePreview }>('POST', '/marketing/audience', { ...audience, postalCodePrefix: audience.postalCodePrefix || undefined })).data,
    enabled: editable && (audience.postalCodePrefix ?? '').match(/^\d*$/) !== null,
    placeholderData: keepPreviousData,
  });
  const recipients = useQuery({
    queryKey: ['marketing-recipients', id, recipientStatus, recipientPage],
    queryFn: () => api<Paginated<CampaignRecipient>>('GET', `/marketing/campaigns/${id}/recipients${queryString({ status: recipientStatus, page: recipientPage, perPage: 25 })}`),
    enabled: !isNew && !!c && c.status !== 'draft',
    placeholderData: keepPreviousData,
    refetchInterval: c?.status === 'sending' ? 4000 : false,
  });

  const dirty = useMemo(() => !c || c.name !== name || c.subject !== subject || c.body !== body || JSON.stringify(c.audience) !== audienceKey, [c, name, subject, body, audienceKey]);
  const refresh = () => {
    for (const key of ['marketing-campaigns', 'marketing-campaign', 'marketing-overview', 'marketing-recipients']) client.invalidateQueries({ queryKey: [key] });
  };
  const payload = () => ({ name: name.trim(), subject: subject.trim(), body: body.trim(), audience: { ...audience, postalCodePrefix: audience.postalCodePrefix || undefined } });
  const fail = (fallback: string) => (e: unknown) => setError(errorText(e, fallback));

  const save = useMutation({
    mutationFn: async () => (isNew ? api<{ data: Campaign }>('POST', '/marketing/campaigns', payload()) : api<{ data: Campaign }>('PATCH', `/marketing/campaigns/${id}`, payload())).then((r) => r.data),
    onSuccess: (saved) => {
      refresh();
      setError(null);
      setNotice('Gespeichert.');
      if (isNew) navigate(`/marketing/kampagnen/${saved.id}`, { replace: true });
    },
    onError: fail('Das Speichern hat nicht geklappt.'),
  });
  const test = useMutation({
    mutationFn: () => api('POST', `/marketing/campaigns/${id}/test`, { to: testTo.trim() }),
    onSuccess: () => { setError(null); setNotice(`Die Testmail ging an ${testTo.trim()}.`); },
    onError: fail('Die Testmail konnte nicht gesendet werden.'),
  });
  const send = useMutation({
    mutationFn: () => api('POST', `/marketing/campaigns/${id}/send`),
    onSuccess: () => { refresh(); setError(null); setNotice('Der Versand läuft. Sie können diese Seite verlassen.'); },
    onError: fail('Der Versand konnte nicht gestartet werden.'),
  });
  const cancel = useMutation({
    mutationFn: () => api('POST', `/marketing/campaigns/${id}/cancel`),
    onSuccess: () => { refresh(); setNotice('Der Versand wurde abgebrochen. Bereits gesendete Mails lassen sich nicht zurückholen.'); },
    onError: fail('Das Abbrechen hat nicht geklappt.'),
  });
  const duplicate = useMutation({
    mutationFn: async () => (await api<{ data: Campaign }>('POST', `/marketing/campaigns/${id}/duplicate`)).data,
    onSuccess: (copy) => { refresh(); navigate(`/marketing/kampagnen/${copy.id}`); },
    onError: fail('Das Duplizieren hat nicht geklappt.'),
  });
  const remove = useMutation({
    mutationFn: () => api('DELETE', `/marketing/campaigns/${id}`),
    onSuccess: () => { refresh(); navigate('/marketing'); },
    onError: fail('Das Löschen hat nicht geklappt.'),
  });

  const insert = (token: string) => {
    const el = bodyField.current;
    if (!el) return setBody((b) => b + token);
    const start = el.selectionStart ?? body.length;
    const end = el.selectionEnd ?? body.length;
    setBody(body.slice(0, start) + token + body.slice(end));
    requestAnimationFrame(() => { el.focus(); el.setSelectionRange(start + token.length, start + token.length); });
  };
  const toggleKind = (kind: 'person' | 'company') => setAudience((a) => ({ ...a, kinds: a.kinds.includes(kind) ? a.kinds.filter((k) => k !== kind) : [...a.kinds, kind] }));
  const submit = (event: FormEvent) => {
    event.preventDefault();
    setNotice(null);
    setError(null);
    save.mutate();
  };
  const company = session?.tenant.name ?? 'Ihr Unternehmen';
  const count = reach.data?.count ?? 0;

  if (!isNew && campaign.isLoading) return <p className="muted">Wird geladen …</p>;
  if (!isNew && campaign.isError) return <div className="alert alert--error" role="alert">{(campaign.error as Error).message}</div>;

  return (
    <>
      <div className="page-head">
        <h1>{isNew ? 'Neue Kampagne' : c?.name}</h1>
        {c && <CampaignBadge status={c.status} />}
        <Link to="/marketing" className="btn btn--secondary">Zur Übersicht</Link>
      </div>
      {notice && <div className="alert alert--ok" role="status">{notice}</div>}
      {error && <div className="alert alert--error" role="alert">{error}</div>}

      {c && c.status !== 'draft' && (
        <section className="card" aria-labelledby="mk-result">
          <h2 id="mk-result">{c.status === 'sending' ? 'Versand läuft' : 'Bilanz'}</h2>
          <div className="mk-tiles mk-tiles--inline">
            <div className="mk-tile"><span className="muted small">Empfänger</span><strong>{c.stats.recipients}</strong></div>
            <div className="mk-tile"><span className="muted small">Gesendet</span><strong>{c.stats.sent}</strong><span className="muted small">{percent(c.stats.sent, c.stats.recipients)}</span></div>
            <div className="mk-tile"><span className="muted small">Fehlgeschlagen</span><strong className={c.stats.failed > 0 ? 'mk-bad' : ''}>{c.stats.failed}</strong></div>
            {c.stats.queued > 0 && <div className="mk-tile"><span className="muted small">Wartet noch</span><strong>{c.stats.queued}</strong></div>}
            <div className="mk-tile"><span className="muted small">Abmeldungen</span><strong>{c.stats.unsubscribed}</strong><span className="muted small">{percent(c.stats.unsubscribed, c.stats.sent)} der gesendeten</span></div>
          </div>
          {c.status === 'sending' && c.stats.recipients > 0 && <progress className="mk-progress" max={c.stats.recipients} value={c.stats.sent + c.stats.failed} aria-label="Fortschritt des Versands" />}
          <p className="muted small">Gestartet {when(c.startedAt)}{c.finishedAt && <> · beendet {when(c.finishedAt)}</>}{c.createdByName && <> · angelegt von {c.createdByName}</>}</p>
          <div className="form-actions">
            {c.status === 'sending' && can('marketing.send') && <button type="button" className="btn btn--danger" onClick={() => window.confirm('Den Versand abbrechen? Mails, die noch warten, werden nicht mehr gesendet.') && cancel.mutate()}>Versand abbrechen</button>}
            {can('marketing.write') && <button type="button" className="btn btn--secondary" onClick={() => duplicate.mutate()} disabled={duplicate.isPending}>Duplizieren</button>}
            {can('marketing.delete') && c.status === 'cancelled' && <button type="button" className="btn btn--danger" onClick={() => window.confirm('Diese Kampagne löschen?') && remove.mutate()}>Löschen</button>}
          </div>
        </section>
      )}

      <form className="mk-columns" onSubmit={submit} aria-label="Kampagne">
        <div className="mk-main">
          <section className="card mk-form" aria-labelledby="mk-text">
            <h2 id="mk-text">Nachricht</h2>
            <label className="field">
              <span>Interner Name</span>
              <input className="input" value={name} onChange={(e) => setName(e.target.value)} maxLength={120} required disabled={!editable} placeholder="Zum Beispiel: Herbstaktion 2026" />
            </label>
            <label className="field">
              <span>Betreff</span>
              <input className="input" value={subject} onChange={(e) => setSubject(e.target.value)} maxLength={200} required disabled={!editable} placeholder="Der Betreff, den der Empfänger sieht" />
            </label>
            <label className="field">
              <span>Text</span>
              <textarea ref={bodyField} className="input mk-body" value={body} onChange={(e) => setBody(e.target.value)} rows={12} maxLength={20000} required disabled={!editable} placeholder={'Guten Tag {{name}},\n\nschön, dass Sie dabei sind …'} />
            </label>
            {editable && (
              <div className="mk-insert">
                <span className="muted small">Einfügen:</span>
                {CAMPAIGN_PLACEHOLDERS.map((p) => <button key={p.key} type="button" className="btn btn--secondary btn--sm" onClick={() => insert(p.key)} title={p.label}>{p.key}</button>)}
              </div>
            )}
            <p className="muted small">Leere Zeilen trennen Absätze. Internetadressen werden anklickbar. Absender und Abmeldelink hängt Wirkia automatisch unten an, das ist gesetzlich vorgeschrieben.</p>
          </section>

          <section className="card mk-form" aria-labelledby="mk-audience">
            <h2 id="mk-audience">Empfänger</h2>
            <p className="muted small">Es erhalten nur angemeldete Abonnenten die Kampagne. Jede Bedingung engt den Kreis weiter ein. Ohne Bedingung bekommen alle die Nachricht.</p>
            <div className="field">
              <span>Beziehungsart des Kontakts</span>
              {editable ? <RelationPicker value={audience.relationTypeIds} onChange={(ids) => setAudience((a) => ({ ...a, relationTypeIds: ids }))} /> : <span>{audience.relationTypeIds.length ? `${audience.relationTypeIds.length} gewählt` : 'Alle'}</span>}
              <span className="muted small">Mehrere Arten gelten als „oder“: Wer mindestens eine trägt, gehört dazu.</span>
            </div>
            <fieldset className="mk-choice mk-choice--row" disabled={!editable}>
              <legend>Art des Kontakts</legend>
              <label><input type="checkbox" checked={audience.kinds.includes('person')} onChange={() => toggleKind('person')} /> Personen</label>
              <label><input type="checkbox" checked={audience.kinds.includes('company')} onChange={() => toggleKind('company')} /> Firmen</label>
            </fieldset>
            <label className="field mk-narrow">
              <span>Postleitzahl beginnt mit</span>
              <input className="input" inputMode="numeric" value={audience.postalCodePrefix ?? ''} onChange={(e) => setAudience((a) => ({ ...a, postalCodePrefix: e.target.value.replace(/\D/g, '').slice(0, 5) }))} maxLength={5} disabled={!editable} placeholder="Zum Beispiel 844" />
            </label>
            {editable && (
              <p className={`mk-reach${count === 0 ? ' mk-reach--zero' : ''}`} role="status">
                {reach.data ? <>Erreicht <strong>{count.toLocaleString('de-DE')}</strong> von {reach.data.subscribed.toLocaleString('de-DE')} angemeldeten Abonnenten.</> : 'Wird gezählt …'}
              </p>
            )}
          </section>
        </div>

        <div className="mk-side">
          <section className="card" aria-labelledby="mk-preview">
            <h2 id="mk-preview">Vorschau</h2>
            <div className="mk-mail">
              <div className="mk-mail__subject">{preview(subject, company) || <span className="muted">Betreff</span>}</div>
              <div className="mk-mail__body">{preview(body, company) || <span className="muted">Hier erscheint Ihr Text.</span>}</div>
              <div className="mk-mail__foot">{company} · Anschrift aus Ihren Firmendaten<br />Sie erhalten diese E-Mail, weil Sie sich für den Newsletter angemeldet haben. Vom Newsletter abmelden</div>
            </div>
            <p className="muted small">Muster mit dem Namen „Max Mustermann“. Die Firmendaten im Fuß stammen aus Einrichtung → Firmendaten.</p>
          </section>

          {editable && (
            <section className="card mk-form" aria-labelledby="mk-actions">
              <h2 id="mk-actions">Speichern und senden</h2>
              <div className="form-actions">
                <button type="submit" className="btn btn--primary" disabled={save.isPending || (!isNew && !dirty)}>{save.isPending ? 'Wird gespeichert …' : 'Speichern'}</button>
                {!isNew && can('marketing.delete') && <button type="button" className="btn btn--danger" onClick={() => window.confirm('Diesen Entwurf löschen?') && remove.mutate()}>Löschen</button>}
              </div>
              {!isNew && (
                <>
                  <div className="field">
                    <span>Testmail an</span>
                    <div className="mk-inline">
                      <input className="input" type="email" value={testTo} onChange={(e) => setTestTo(e.target.value)} placeholder="ihre@adresse.de" aria-label="Adresse für die Testmail" />
                      <button type="button" className="btn btn--secondary" disabled={test.isPending || !testTo.trim() || dirty} onClick={() => { setNotice(null); test.mutate(); }}>Test senden</button>
                    </div>
                    <span className="muted small">Die Testmail geht nur an diese Adresse und zählt nicht als Versand.{dirty && ' Speichern Sie zuerst Ihre Änderungen.'}</span>
                  </div>
                  {can('marketing.send') ? (
                    <div className="field">
                      <button type="button" className="btn btn--primary mk-send" disabled={send.isPending || dirty || count === 0} onClick={() => window.confirm(`Die Kampagne „${name}“ jetzt an ${count} Abonnenten versenden? Das lässt sich nicht zurückholen.`) && send.mutate()}>
                        {send.isPending ? 'Wird gestartet …' : `An ${count.toLocaleString('de-DE')} Abonnenten versenden`}
                      </button>
                      <span className="muted small">{dirty ? 'Speichern Sie zuerst Ihre Änderungen. ' : ''}Versenden Sie vorher eine Testmail an sich selbst.</span>
                    </div>
                  ) : (
                    <p className="muted small">Zum Versenden fehlt Ihrer Rolle das Recht „Kampagnen versenden“.</p>
                  )}
                </>
              )}
              {isNew && <p className="muted small">Nach dem Speichern können Sie eine Testmail schicken und die Kampagne versenden.</p>}
            </section>
          )}
        </div>
      </form>

      {c && c.status !== 'draft' && (
        <section className="card" aria-labelledby="mk-recipients">
          <div className="section-head">
            <h2 id="mk-recipients">Empfänger</h2>
            <select className="input mk-status" value={recipientStatus} onChange={(e) => { setRecipientStatus(e.target.value); setRecipientPage(1); }} aria-label="Stand der Mail">
              <option value="">Alle</option>
              {RECIPIENT_STATUSES.map((s) => <option key={s} value={s}>{RECIPIENT_STATUS_LABELS[s]}</option>)}
            </select>
          </div>
          <div className="table-card">
            <table className="table table--cards">
              <thead><tr><th>Name</th><th>E-Mail</th><th>Stand</th><th>Gesendet</th></tr></thead>
              <tbody>
                {recipients.isLoading && <tr><td colSpan={4} className="muted">Wird geladen …</td></tr>}
                {recipients.data?.data.length === 0 && <tr><td colSpan={4} className="empty">Keine Einträge.</td></tr>}
                {recipients.data?.data.map((r) => (
                  <tr key={r.id}>
                    <td data-label="Name">{r.contactId ? <Link to={`/kontakte/${r.contactId}`} className="strong-link">{r.name}</Link> : r.name}</td>
                    <td data-label="E-Mail">{r.email}</td>
                    <td data-label="Stand"><RecipientBadge status={r.status} />{r.unsubscribed && <span className="mk-badge mk-badge--off">Abgemeldet</span>}{r.error && <div className="muted small">{r.error}</div>}</td>
                    <td data-label="Gesendet">{when(r.sentAt)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {recipients.data && recipients.data.meta.lastPage > 1 && (
            <div className="pager">
              <button type="button" className="btn btn--secondary btn--sm" disabled={recipientPage <= 1} onClick={() => setRecipientPage(recipientPage - 1)}>Zurück</button>
              <span className="muted">Seite {recipientPage} von {recipients.data.meta.lastPage}</span>
              <button type="button" className="btn btn--secondary btn--sm" disabled={recipientPage >= recipients.data.meta.lastPage} onClick={() => setRecipientPage(recipientPage + 1)}>Weiter</button>
            </div>
          )}
          <p className="muted small">Angelegt {day(c.createdAt)}. Fehlgeschlagene Mails werden nicht automatisch wiederholt.</p>
        </section>
      )}
    </>
  );
}
